import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    requestCreatePassiveMapper,
    requestRemovePassiveMapper,
    requestStartMapper,
    requestStopMapper,
    requestUpdatePassiveMapper,
} from "./msgs/MsgSender";
import {
    AnyMapper,
    EdgeMapperComp,
    isEdgeMapper,
    PassiveMapperComp,
    MixerMapperComp,
} from "userful-chronos-app-common-js/dist/models/mapping/MappingGroups";
import { StringID } from "userful-chronos-app-common-js/dist/models/common";
import { v4 as uuidv4 } from "uuid";
import { PassiveCollectionComp } from "userful-chronos-app-common-js/dist/models/mapping/PassiveCollection";

export interface EventsState {
    mappers: PassiveMapperComp[];
    activeMixerMappers: MixerMapperComp[];
    activeEdgeMappers: EdgeMapperComp[];
    startingMappers: StringID[];
    stoppingMappers: StringID[];
    passiveCollections: PassiveCollectionComp[];
}

const initialState: EventsState = {
    mappers: [],
    activeMixerMappers: [],
    activeEdgeMappers: [],
    startingMappers: [],
    stoppingMappers: [],
    passiveCollections: [],
};

const pendingStartMapper: string[] = [];
const addPending = (id: StringID) => {
    if (pendingStartMapper.includes(id.value)) {
        return;
    }
    pendingStartMapper.push(id.value);
};

const removePending = (id: StringID): string => {
    const foundIndex = pendingStartMapper.indexOf(id.value);
    if (foundIndex >= 0) {
        return pendingStartMapper.splice(foundIndex, 1)[0];
    }
    return null;
};

const executeNextPending = () => {
    const next = pendingStartMapper.shift();
    if (next) {
        requestStartMapper({ value: next });
    }
};

const executeAferDelay = (id: StringID) => {
    setTimeout(() => {
        const timedOutItem = removePending(id);
        if (timedOutItem) {
            requestStartMapper({ value: timedOutItem });
        }
    }, 5000);
};

export const passiveMapperSlice = createSlice({
    name: "passiveMapperSlice",
    initialState,
    reducers: {
        setMappers: (state, action: PayloadAction<PassiveMapperComp[]>) => {
            state.mappers = [...action.payload];
        },
        notifyMapperAdded: (state, action: PayloadAction<PassiveMapperComp>) => {
            state.mappers = [...state.mappers, { ...action.payload }];
        },
        notifyMapperDeleted: (state, action: PayloadAction<StringID>) => {
            state.mappers = state.mappers.filter((item) => item.id.value !== action.payload.value);
        },
        notifyMapperUpdated: (state, action: PayloadAction<PassiveMapperComp>) => {
            const foundIndex = state.mappers.findIndex((item) => item.id.value === action.payload.id.value);
            if (foundIndex >= 0) {
                state.mappers = [
                    ...state.mappers.slice(0, foundIndex),
                    { ...action.payload },
                    ...state.mappers.slice(foundIndex + 1),
                ];
            }
        },

        setPassiveCollections: (state, action: PayloadAction<PassiveCollectionComp[]>) => {
            state.passiveCollections = [...action.payload];
        },
        notifyPassiveCollectionAdded: (state, action: PayloadAction<PassiveCollectionComp>) => {
            state.passiveCollections = [...state.passiveCollections, { ...action.payload }];
        },
        notifyPassiveCollectionDeleted: (state, action: PayloadAction<StringID>) => {
            state.passiveCollections = state.passiveCollections.filter(
                (item) => item.id.value !== action.payload.value,
            );
        },
        notifyPassiveCollectionUpdated: (state, action: PayloadAction<PassiveCollectionComp>) => {
            const foundIndex = state.passiveCollections.findIndex((item) => item.id.value === action.payload.id.value);
            if (foundIndex >= 0) {
                state.passiveCollections = [
                    ...state.passiveCollections.slice(0, foundIndex),
                    { ...action.payload },
                    ...state.passiveCollections.slice(foundIndex + 1),
                ];
            }
        },

        setMixerMappers: (state, action: PayloadAction<MixerMapperComp[]>) => {
            state.activeMixerMappers = [...action.payload];
        },

        setEdgeMappers: (state, action: PayloadAction<EdgeMapperComp[]>) => {
            state.activeEdgeMappers = [...action.payload];
        },

        notifyActiveMapperAdded: (state, action: PayloadAction<AnyMapper>) => {
            removePending(action.payload.origin.id);
            executeNextPending();
            if (isEdgeMapper(action.payload)) {
                state.activeEdgeMappers = [...state.activeEdgeMappers, { ...action.payload } as EdgeMapperComp];
            } else {
                state.activeMixerMappers = [...state.activeMixerMappers, { ...action.payload } as MixerMapperComp];
            }
            state.startingMappers = state.startingMappers.filter(
                (item) => item.value !== action.payload.origin?.id.value,
            );
        },
        notifyActiveMapperDeleted: (state, action: PayloadAction<StringID>) => {
            const foundActiveMapper =
                state.activeEdgeMappers.find((item) => item.id.value === action.payload.value) ||
                state.activeMixerMappers.find((item) => item.id.value === action.payload.value);
            state.activeEdgeMappers = state.activeEdgeMappers.filter((item) => item.id.value !== action.payload.value);
            state.activeMixerMappers = state.activeMixerMappers.filter(
                (item) => item.id.value !== action.payload.value,
            );
            state.stoppingMappers = state.stoppingMappers.filter(
                (item) => item.value !== foundActiveMapper?.origin?.id.value,
            );
        },
        notifyActiveMapperUpdated: (state, action: PayloadAction<AnyMapper>) => {
            if (isEdgeMapper(action.payload)) {
                const foundIndex = state.activeEdgeMappers.findIndex(
                    (item) => item.id.value === action.payload.id.value,
                );
                if (foundIndex >= 0) {
                    state.activeEdgeMappers = [
                        ...state.activeEdgeMappers.slice(0, foundIndex),
                        { ...action.payload } as EdgeMapperComp,
                        ...state.activeEdgeMappers.slice(foundIndex + 1),
                    ];
                }
            } else {
                const foundIndex = state.activeMixerMappers.findIndex(
                    (item) => item.id.value === action.payload.id.value,
                );
                if (foundIndex >= 0) {
                    state.activeMixerMappers = [
                        ...state.activeMixerMappers.slice(0, foundIndex),
                        { ...action.payload } as MixerMapperComp,
                        ...state.activeMixerMappers.slice(foundIndex + 1),
                    ];
                }
            }
        },

        createMapper: (state, action: PayloadAction<PassiveMapperComp>) => {
            // need to reset inset's ids to prevent UI from sending the same id after editing
            const payload: PassiveMapperComp = {
                ...action.payload,
                insets: action.payload.insets.map((item) => ({
                    ...item,
                    id: { value: uuidv4() },
                })),
            };
            requestCreatePassiveMapper(payload);
        },
        updateMapper: (state, action: PayloadAction<PassiveMapperComp>) => {
            if (state.mappers.findIndex((item) => item.id.value === action.payload.id.value) >= 0) {
                requestUpdatePassiveMapper(action.payload);
            } else {
                requestCreatePassiveMapper(action.payload);
            }
        },
        deleteMapper: (state, action: PayloadAction<StringID>) => {
            requestRemovePassiveMapper(action.payload);
        },
        startMapper: (state, action: PayloadAction<StringID>) => {
            const payload = { ...action.payload };
            if (state.startingMappers.length < 1) {
                requestStartMapper(payload);
            } else {
                addPending(payload);
                executeAferDelay(payload);
            }
            state.startingMappers = [...state.startingMappers, action.payload];
        },
        stopMapper: (state, action: PayloadAction<StringID>) => {
            state.stoppingMappers = [...state.stoppingMappers, action.payload];
            requestStopMapper(action.payload);
        },
    },
});

export const passiveMapperActions = passiveMapperSlice.actions;

export default passiveMapperSlice.reducer;
